import { useState } from "react";
import { CreditCard, Lock, Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

/**
 * Reusable simulated card payment form.
 * Props:
 *  - amount (string, e.g. "9,99 €")
 *  - title, subtitle
 *  - onPay(cardPayload) => Promise
 *  - onCancel()
 *  - ctaLabel
 */
export default function PaymentForm({ amount, title, subtitle, onPay, onCancel, ctaLabel = "Payer" }) {
  const [n, setN] = useState("");
  const [name, setName] = useState("");
  const [exp, setExp] = useState("");
  const [cvv, setCvv] = useState("");
  const [busy, setBusy] = useState(false);

  const format = (v) => v.replace(/\D/g, "").slice(0, 19).replace(/(.{4})/g, "$1 ").trim();
  const formatExp = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 4);
    if (d.length < 3) return d;
    return `${d.slice(0, 2)}/${d.slice(2)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!n || !exp || !cvv || !name) {
      toast.error("Merci de remplir tous les champs de la carte");
      return;
    }
    setBusy(true);
    try {
      await onPay({ card_number: n.replace(/\s/g, ""), card_exp: exp, card_cvv: cvv, card_name: name });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Paiement refusé");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="payment-form">
      <div className="bg-ink text-white rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <CreditCard className="w-6 h-6" />
          <div className="text-[10px] uppercase tracking-widest text-white/50">Paiement sécurisé</div>
        </div>
        <div className="font-heading font-bold text-2xl">{amount}</div>
        {title && <div className="text-sm text-white/70 mt-1">{title}</div>}
        {subtitle && <div className="text-[11px] text-white/50 mt-0.5">{subtitle}</div>}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-slate2">Nom sur la carte</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Marc Dupont" className="rounded-xl h-12" data-testid="pay-name-input" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-slate2">Numéro de carte</Label>
        <div className="relative">
          <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input inputMode="numeric" value={n} onChange={(e) => setN(format(e.target.value))} placeholder="4242 4242 4242 4242" className="pl-10 rounded-xl h-12 font-mono tracking-wider" data-testid="pay-card-input" />
        </div>
        <div className="text-[11px] text-slate2">💳 Test : 4242 4242 4242 4242 · 12/29 · 123</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate2">Expiration</Label>
          <Input inputMode="numeric" value={exp} onChange={(e) => setExp(formatExp(e.target.value))} placeholder="MM/AA" className="rounded-xl h-12 font-mono" data-testid="pay-exp-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate2">CVV</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input inputMode="numeric" maxLength={4} value={cvv} onChange={(e) => setCvv(e.target.value.replace(/\D/g, ""))} placeholder="123" className="pl-10 rounded-xl h-12 font-mono" data-testid="pay-cvv-input" />
          </div>
        </div>
      </div>

      <Button type="submit" disabled={busy} className="w-full rounded-full bg-pro hover:bg-pro-hover text-white py-6 text-base font-semibold" data-testid="pay-submit-btn">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Traitement…</> : <>Payer {ctaLabel === "Payer" ? amount : ctaLabel}</>}
      </Button>

      {onCancel && (
        <Button type="button" onClick={onCancel} variant="ghost" className="w-full rounded-full" data-testid="pay-cancel-btn">
          Annuler
        </Button>
      )}

      <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate2 pt-2">
        <ShieldCheck className="w-3.5 h-3.5 text-forest" /> Paiement sécurisé · Aucun débit en mode démo
      </div>
    </form>
  );
}
